import { useEffect, useRef, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { useGetLeaveRequest } from "../../../../services/leave-request.service";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import {
	LEAVE_REQUEST_OPTIONS,
	LeaveRequestDto,
} from "../../../../models/leave-request.dto";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import { Popover } from "@mui/material";
import { LuEye } from "react-icons/lu";
import { FiSliders } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import LeaveRequestDetailsModal from "../../../self_service/leave_management/modals/LeaveRequestDetailsModal";
import LeaveRequestActionModal from "../../../self_service/leave_management/modals/LeaveRequestActionModal";
import useUserStore from "../../../../state-management/useUserStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { useGetEmployee } from "../../../../services/employee.service";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import HrLeaveRequestModalForm from "../modals/HrLeaveRequestModalForm";
import { AiOutlinePlus } from "react-icons/ai";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import UploadLeaveRequests from "../modals/UploadLeaveRequests";
import { GoUpload } from "react-icons/go";
import { toast } from "react-toastify";
import { getAccessToken } from "../../../../services/utils/app.data";
import ENDPOINTS from "../../../../services/utils/endpoints";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	PermissionChecker,
	PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import {
	leaveRequestsColumnsHR,
	leaveRequestColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportLeaveRequestsModal from "../modals/ExportLeaveRequestsModal";

const LeaveRequestsView = () => {
	const { currentCompany } = useCompanyStore();
	const { userType, currentUser } = useUserStore();
	const companyProfile = (currentUser.user.companyProfiles || [])[0];
	const [searchParams] = useSearchParams();
	const requestId = searchParams.get("requestId");
	const { formDataValues, setFormData } = useEditFormStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openUploadLeaveRequests, setOpenUploadLeaveRequests] = useState(false);
	const [openLeaveRequestModalForm, setOpenLeaveRequestModalForm] =
		useState(false);
	const [openLeaveRequestDetailsModal, setOpenLeaveRequestDetailsModal] =
		useState(false);
	const [openRequestActionModal, setOpenRequestActionModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(leaveRequestColumnVisibility);

	useEffect(() => {
		if (requestId) {
			setOpenLeaveRequestDetailsModal(true);
		}
	}, [requestId]);

	const { tableAPIRef, tableData, isLoading, pageCount, setTableData } =
		useManageTableWithSearchParams<LeaveRequestDto>({
			fetchHook: useGetLeaveRequest,
			options: { queryMode: "ALL" },
		});

	// Fetched data for select fields requiring data from database
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenLeaveRequestModalForm(true);
	};

	const { reloadTableData } = useManageTableWithSearchParamsData();

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleOpenExportModal = () => {
		setIsOpen(false);
		setOpenExportModal(true);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<>
			<div className="flex items-center justify-between mb-8">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{
									title: "Start Date",
									fieldName: "startDate",
								},
								{
									title: "End Date",
									fieldName: "returnDate",
								},
								{
									title: "Date Created",
									fieldName: "createdAt",
								},
								{
									title: "Date Modified",
									fieldName: "modifiedAt",
								},
							],
						},
						{
							title: "Employee",
							fieldName: "employeeId",
							fieldType: "SELECT_FIELD",
							options: employees?.data || [],
							mapTexts: ["firstName", "lastName"],
						},
						{
							title: "Status",
							fieldName: "status",
							fieldType: "CHECKBOX_LIST",
							options: LEAVE_REQUEST_OPTIONS,
						},
						{
							title: "Start Date",
							fieldName: "startDate",
							fieldType: "DATE_RANGE",
						},
						{
							title: "End Date",
							fieldName: "returnDate",
							fieldType: "DATE_RANGE",
						},
					]}
				/>
				<ManageTableColumns
					columns={leaveRequestsColumnsHR}
					defaultVisibilityModel={leaveRequestColumnVisibility}
					visibilityModel={columnVisibilityModel}
					setVisibilityModel={setColumnVisibilityModel}
					tableApiRef={tableAPIRef}
				/>
				<div className="flex items-center gap-4 font-medium">
					<div className="relative" ref={dropdownRef}>
						{/* Dropdown Trigger Button */}
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{/* Dropdown Menu */}
						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									<PermissionChecker
										permissionRule="OR"
										permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
									>
										<button
											onClick={() => setOpenUploadLeaveRequests(true)}
											className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
														   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
										>
											<GoUpload className="text-[18px] text-gray-500" />
											Upload Leave Requests
										</button>
									</PermissionChecker>
									<button
										onClick={handleOpenExportModal}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
														 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Leave Requests
									</button>
								</div>
							</div>
						)}
					</div>
					<button
						onClick={handleOpenModal}
						className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white"
					>
						<AiOutlinePlus className="mr-2 text-lg" />
						Add New Leave Request
					</button>
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.LEAVE_REQUESTS}
				data={tableData}
				columns={leaveRequestsColumnsHR}
				columnVisibility={columnVisibilityModel} // ✅ Add this
				onColumnVisibilityChange={setColumnVisibilityModel} // ✅ Add this
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={leaveRequestColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							setOpenLeaveRequestDetailsModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<LuEye />
						<p className="text-2sm">View Details</p>
					</div>
					{/* {(formDataValues?.status === "PENDING") && (
						<div
							onClick={() => {
								setIsEditForm(true);
								setOpenLeaveRequestModalForm(true);
								handleClose();
							}}
							className="table-action-popover"
						>
							<BsPencil />
							<p className="text-2sm">Edit</p>
						</div>
					)} */}
					{formDataValues?.status === "APPROVED" &&
						(formDataValues?.employee?.id !== companyProfile?.employeeId ||
							userType === "ORGANIZATION") && (
							<div
								onClick={() => {
									setOpenRequestActionModal(true);
									handleClose();
								}}
								className="table-action-popover"
							>
								<FiSliders className="text-sm" />
								<p className="text-2sm">Adjust Request</p>
							</div>
						)}
				</div>
			</Popover>

			{openLeaveRequestModalForm && (
				<HrLeaveRequestModalForm
					open={openLeaveRequestModalForm}
					setOpen={setOpenLeaveRequestModalForm}
					title={isEditForm ? "Update Leave Request" : "Create Leave Request"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					employees={employees?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openLeaveRequestDetailsModal && (
				<LeaveRequestDetailsModal
					open={openLeaveRequestDetailsModal}
					title={"Request Details"}
					setOpen={setOpenLeaveRequestDetailsModal}
					tableAPIRef={tableAPIRef}
					supervisor
				/>
			)}
			{openRequestActionModal && (
				<LeaveRequestActionModal
					open={openRequestActionModal}
					setOpen={setOpenRequestActionModal}
					response={false}
					tableAPIRef={tableAPIRef}
				/>
			)}
			{openUploadLeaveRequests && (
				<UploadLeaveRequests
					open={openUploadLeaveRequests}
					setOpen={setOpenUploadLeaveRequests}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportLeaveRequestsModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default LeaveRequestsView;
